import { useCallback, useRef, useState } from "react";

/**
 * Stores snapshots of a value and supports undo/redo.
 * - Push snapshots only when you decide (manual, avoids lag)
 * - Good for JSON objects (stores deep clones)
 */
export default function useUndoRedo(initialValue, { limit = 50 } = {}) {
    const pastRef = useRef([]);
    const futureRef = useRef([]);

    const [present, setPresent] = useState(initialValue);

    const set = useCallback((nextValue, { pushHistory = true } = {}) => {
        setPresent((current) => {
            const next = typeof nextValue === "function" ? nextValue(current) : nextValue;

            if (pushHistory) {
                pastRef.current.push(structuredClone(current));
                if (pastRef.current.length > limit) pastRef.current.shift();
                futureRef.current = [];
            }

            return next;
        });
    }, [limit]);

    const undo = useCallback(() => {
        setPresent((current) => {
            const past = pastRef.current;
            if (past.length === 0) return current;

            const previous = past.pop();
            futureRef.current.push(structuredClone(current));
            return previous;
        });
    }, []);

    const redo = useCallback(() => {
        setPresent((current) => {
            const future = futureRef.current;
            if (future.length === 0) return current;

            const next = future.pop();
            pastRef.current.push(structuredClone(current));
            return next;
        });
    }, []);

    const canUndo = pastRef.current.length > 0;
    const canRedo = futureRef.current.length > 0;

    const reset = useCallback((value) => {
        pastRef.current = [];
        futureRef.current = [];
        setPresent(value);
    }, []);

    return {
        present,
        set,
        undo,
        redo,
        canUndo,
        canRedo,
        reset,
    };
}
